import type { RequestConfig } from "@app/config/axios/http";
import { lotsServices, type ApiStockAvailableLot, type ApiStockLot } from "../../../../../api/lots";
import { storePlacesServices, type StorePlaceRemainder } from "../../../../../api/storePlaces";
import type { StockKanbanContainer } from "../../../../../api/stockKanbanContainers";
import {
    buildStockKanbanSupplyAvailableLotFilter,
    filterStockKanbanSupplyAvailableLots,
    resolveStockKanbanSupplyLotQtyRest,
} from "../../../helpers/stockKanbanSupplyAvailableLot";
import { resolveStockKanbanSupplyLotCells, uniqueRemaindersBySpCode } from "./resolveStockKanbanSupplyLotCells";

/**
 * Остатки выбранной партии контейнера КАНБАН — всё, что нужно форме комплектования
 * для динамического поведения (размещение, лимит qty).
 */
export interface StockKanbanSupplyLotAvailability {
    /**
     * Остатки партии по ячейкам склада-отправителя. \
     * `null` — партия без размещения («на полу»), ячеек с остатком нет.
     */
    cells: StorePlaceRemainder[] | null;
    /** Доступный остаток партии из `getAvLots` в ЕИ контейнера. */
    lotQtyRest: number;
}

/**
 * Опция доменного селекта партии КАНБАН: доступная партия + её остатки по ячейкам. \
 * Выбор такой опции даёт форме всё нужное синхронно, без догрузки.
 */
export type StockKanbanSupplyAvailableLotOption = ApiStockAvailableLot & {
    /** Данные обычной партии (номер и т.п.) для отображения. */
    Lot?: ApiStockLot;
    /** Остатки партии по ячейкам склада-отправителя + доступный остаток. */
    availability: StockKanbanSupplyLotAvailability;
};

/**
 * Загружает доступные партии контейнера сразу с остатками по ячейкам: \
 * `getAvLots` + карточки партий + один батч `loadRemaindersByFilter` по всем партиям. \
 * Без `DivisionCode`: у контейнера это подразделение-потребитель, а ячейки — склада-отправителя
 * (склад уже в коде партии).
 * @param container контейнер КАНБАН (ТМЦ/ЕИ фильтра остатков)
 * @param config опции HTTP-запроса
 */
export async function loadStockKanbanSupplyAvailableLotOptions(
    container: StockKanbanContainer,
    config?: RequestConfig,
): Promise<StockKanbanSupplyAvailableLotOption[]> {
    const availableLots = filterStockKanbanSupplyAvailableLots(
        await lotsServices.getAvailableLots({ flt: buildStockKanbanSupplyAvailableLotFilter(container) }, config),
    );

    if (availableLots.length === 0) {
        return [];
    }

    const lotCodes = uniqueLotCodes(availableLots.map((item) => item.LotCode));

    const [lots, remainders] = await Promise.all([
        lotsServices.load({ flt: { Codes: lotCodes } }, config),
        storePlacesServices.remainders.load(
            {
                filter: {
                    StockobjCodes: [container.StockobjCode],
                    LotCodes: lotCodes,
                    OnlyPositiveRemainders: true,
                    InContextOfLot: true,
                },
                include: ["StorePlace"],
            },
            config,
        ),
    ]);

    const lotsByCode = new Map(lots.map((lot) => [lot.LotCode, lot]));
    const cellsByLotCode = groupRemaindersByLotCode(remainders);

    return availableLots.map((availableLot) => ({
        ...availableLot,
        Lot: lotsByCode.get(availableLot.LotCode),
        availability: {
            cells: cellsByLotCode.get(availableLot.LotCode) ?? null,
            lotQtyRest: resolveStockKanbanSupplyLotQtyRest(availableLot),
        },
    }));
}

/**
 * Резолвит одну обогащённую опцию по коду партии (byId для селекта и edit-режима формы). \
 * Если партия выпала из `getAvLots` (например, уже полностью распределена), возвращает
 * label-only опцию с остатками по ячейкам и нулевым доступным остатком.
 * @param container контейнер КАНБАН
 * @param lotCode код партии
 * @param config опции HTTP-запроса
 */
export async function loadStockKanbanSupplyAvailableLotByCode(
    container: StockKanbanContainer,
    lotCode: number,
    config?: RequestConfig,
): Promise<StockKanbanSupplyAvailableLotOption | null> {
    const options = await loadStockKanbanSupplyAvailableLotOptions(container, config);
    const availableOption = options.find((option) => option.LotCode === lotCode);

    if (availableOption != null) {
        return availableOption;
    }

    const [lots, cells] = await Promise.all([
        lotsServices.load({ flt: { Codes: [lotCode] } }, config),
        resolveStockKanbanSupplyLotCells(lotCode, config),
    ]);
    const lot = lots[0];

    if (lot == null) {
        return null;
    }

    return {
        LotCode: lotCode,
        ParentLotCode: 0,
        DepotcardType: 0,
        DepotcardCode: 0,
        AdmissSubstCode: 0,
        LotQtyRest: 0,
        LotQtyRestInNeedMeas: 0,
        Coef: 0,
        Lot: lot,
        availability: { cells: cells.length === 0 ? null : cells, lotQtyRest: 0 },
    };
}

/**
 * Остатки одной партии без батча по списку — для не-React потребителей (сканирование). \
 * `null` — партия недоступна для комплектования этого контейнера.
 * @param container контейнер КАНБАН
 * @param lotCode код партии
 * @param config опции HTTP-запроса
 */
export async function fetchStockKanbanSupplyLotAvailability(
    container: StockKanbanContainer,
    lotCode: number,
    config?: RequestConfig,
): Promise<StockKanbanSupplyLotAvailability | null> {
    const availableLots = filterStockKanbanSupplyAvailableLots(
        await lotsServices.getAvailableLots({ flt: buildStockKanbanSupplyAvailableLotFilter(container) }, config),
    );
    const available = availableLots.find((item) => item.LotCode === lotCode);

    if (available == null) {
        return null;
    }

    const cells = await resolveStockKanbanSupplyLotCells(lotCode, config);

    return { cells: cells.length === 0 ? null : cells, lotQtyRest: resolveStockKanbanSupplyLotQtyRest(available) };
}

function uniqueLotCodes(codes: number[]): number[] {
    return [...new Set(codes)];
}

/** Группирует остатки по партии, оставляя уникальные ячейки с положительным остатком. */
function groupRemaindersByLotCode(remainders: StorePlaceRemainder[]): Map<number, StorePlaceRemainder[]> {
    const byLotCode = new Map<number, StorePlaceRemainder[]>();

    remainders.forEach((remainder) => {
        const bucket = byLotCode.get(remainder.LotCode) ?? [];
        bucket.push(remainder);
        byLotCode.set(remainder.LotCode, bucket);
    });

    byLotCode.forEach((bucket, lotCode) => {
        const uniqueCells = uniqueRemaindersBySpCode(bucket);
        // «Партия без размещения» — явный null, а не пустой массив.
        if (uniqueCells.length === 0) {
            byLotCode.delete(lotCode);
            return;
        }
        byLotCode.set(lotCode, uniqueCells);
    });

    return byLotCode;
}
